/**
 * Sync Status Indicator Component
 * 
 * Toont de synchronisatie-status in de UI.
 * - Groen: Alles gesynchroniseerd
 * - Geel: Bezig met synchroniseren
 * - Oranje: Offline - wijzigingen worden lokaal opgeslagen
 * - Rood: Synchronisatie mislukt
 */

import React from 'react';
import { Cloud, CloudOff, RefreshCw, AlertTriangle, Check, Loader2 } from 'lucide-react';
import { useOfflineQueue } from '../services/offlineQueue';
import { Button } from './ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

export const SyncStatusIndicator = ({ compact = false }) => {
  const { isOnline, isProcessing, pendingCount, failedCount, retryFailed, forceSync } = useOfflineQueue();
  
  // Bepaal status en kleur
  let status = 'synced';
  let color = 'text-green-500';
  let bgColor = 'bg-green-50';
  let borderColor = 'border-green-200';
  let Icon = Check;
  let message = 'Alles gesynchroniseerd';
  
  if (!isOnline) {
    status = 'offline';
    color = 'text-orange-500';
    bgColor = 'bg-orange-50';
    borderColor = 'border-orange-200';
    Icon = CloudOff;
    message = 'Offline - wijzigingen worden lokaal opgeslagen';
  } else if (failedCount > 0) {
    status = 'failed';
    color = 'text-red-500';
    bgColor = 'bg-red-50';
    borderColor = 'border-red-200';
    Icon = AlertTriangle;
    message = `${failedCount} wijziging(en) mislukt`;
  } else if (isProcessing || pendingCount > 0) {
    status = 'syncing';
    color = 'text-blue-500';
    bgColor = 'bg-blue-50';
    borderColor = 'border-blue-200';
    Icon = Loader2;
    message = `${pendingCount} wijziging(en) synchroniseren...`;
  }
  
  // Compact versie (alleen icoon)
  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${bgColor} ${color} cursor-default`}>
              <Icon className={`w-4 h-4 ${isProcessing ? 'animate-spin' : ''}`} />
              {pendingCount > 0 && (
                <span className="text-xs font-medium">{pendingCount}</span>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>{message}</p>
            {failedCount > 0 && (
              <Button 
                size="sm" 
                variant="ghost" 
                className="mt-1 w-full text-xs"
                onClick={retryFailed}
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Opnieuw proberen
              </Button>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  // Volledige versie
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${bgColor} ${borderColor} ${color}`}>
      <Icon className={`w-5 h-5 ${isProcessing ? 'animate-spin' : ''}`} />
      <span className="text-sm font-medium">{message}</span>
      
      {failedCount > 0 && (
        <Button 
          size="sm" 
          variant="outline" 
          className="ml-2 h-7 text-xs"
          onClick={retryFailed}
        >
          <RefreshCw className="w-3 h-3 mr-1" />
          Opnieuw
        </Button>
      )}
      
      {!isOnline && (
        <Button 
          size="sm" 
          variant="outline" 
          className="ml-2 h-7 text-xs"
          onClick={forceSync}
        >
          <Cloud className="w-3 h-3 mr-1" />
          Verbind
        </Button>
      )}
    </div>
  );
};

// Floating versie voor onderaan het scherm
export const SyncStatusFloating = () => {
  const { isOnline, isProcessing, pendingCount, failedCount } = useOfflineQueue();
  
  // Toon alleen als er iets aan de hand is
  const showIndicator = !isOnline || isProcessing || pendingCount > 0 || failedCount > 0;
  
  if (!showIndicator) {
    return null;
  }
  
  return (
    <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-4 duration-300">
      <SyncStatusIndicator />
    </div>
  );
};

export default SyncStatusIndicator;
